/**
 * RAVEN - Auction Tool: AuctionPackages.jsx
 * Module Main Page for Auction Packages.
 * Core container for listing, viewing, and editing auction packages.
 */

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";

// Custom hooks & context
import { useAuctionsData } from "src/hooks/useAuctionsData";
import { useDialogBox } from "src/hooks/useDialogBox";

// Layout/UI
import { PageHeader } from "../SiteLayout/Header";
import { PageFooter } from "../SiteLayout/Footer";
import PackageList from "./PackageList";
import PackageDetails from "./EditPackage";
import { Box, Button, MetreaTooltip } from "@metrea-ui/v2";

// Utilities
import { getQueryVariable } from "src/utils/appUtils";
import { isStrat } from "src/utils/sessionUtils";
import { isUUID } from "src/utils/dataUtils";
import { debugLog } from "src/utils/debugUtils";

// Constants
import { initialAuctionPackageState } from "src/constants/initialStates";

const AuctionPackages = () => {
	const renderCount = useRef(0);
	renderCount.current++;

	// Used for deep linking to an Auction Package by id/uuid in the URL
	const uuid = getQueryVariable("uuid");

	// --- Redux Selectors ---
	// - selectedPkg is the current package (or null if on the list page)
	// - showList is derived from Redux: show list only if nothing selected
	const selectedPkg = useSelector((state) => state.auctions.selectedPkg);
	const showList = useSelector((state) => !state.auctions.selectedPkg);
	const retrievePackagesError = useSelector((state) => state.auctions.error?.retrieve);
	const retrievePackageDetailError = useSelector((state) => state.auctions.error?.retrieveOne);

	// --- Local State ---
	const [pageMode, setPageMode] = useState("liveAuction");
	// Keep the active detail request available for retry even when it came from
	// a list action rather than a UUID query parameter.
	const [detailRequestId, setDetailRequestId] = useState(null);
	const { withError } = useDialogBox();

	// Custom hooks
	const {
		fetchAuctionPackages,
		fetchAuctionPackageByIdOrUuid,
		setSelectedPackage,
		clearSelectedPackage,
	} = useAuctionsData();

	// ---- Effect: Load on mount: show list or package (deep-link support)
	useEffect(() => {
		debugLog(2, "[AuctionPackages][FLOW] mounted", { uuid: uuid, selectedPkg: selectedPkg });

		if (selectedPkg) {
			debugLog(3, "[AuctionPackages][STATE] clearing selected package on mount", {
				selectedPkg: selectedPkg,
			});
			clearSelectedPackage();
		}

		if (!uuid || !isUUID(uuid)) {
			setDetailRequestId(null);
			debugLog(2, "[AuctionPackages][FLOW] fetchAuctionPackages started", {});
			fetchAuctionPackages();
		} else {
			setDetailRequestId(uuid);
			debugLog(2, "[AuctionPackages][FLOW] fetchAuctionPackageByIdOrUuid started", { idOrUuid: uuid });
			fetchAuctionPackageByIdOrUuid(uuid);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleReloadList = useCallback(() => {
		debugLog(3, "[AuctionPackages][ACTION] handleReloadList", {});

		setDetailRequestId(null);
		setPageMode("liveAuction");
		clearSelectedPackage();
		debugLog(2, "[AuctionPackages][FLOW] fetchAuctionPackages started", {});
		fetchAuctionPackages();
	}, [clearSelectedPackage, fetchAuctionPackages]);

	const clickNewPackageHandler = (event) => {
		debugLog(3, "[AuctionPackages][ACTION] clickNewPackageHandler", { eventType: event?.type });

		setPageMode("edit");
		setSelectedPackage(initialAuctionPackageState);
	};

	const handleEditPackage = (id) => {
		debugLog(3, "[AuctionPackages][ACTION] handleEditPackage", { id: id });

		setDetailRequestId(id);
		setPageMode("edit");
		debugLog(2, "[AuctionPackages][FLOW] fetchAuctionPackageByIdOrUuid started", { idOrUuid: id });
		fetchAuctionPackageByIdOrUuid(id);
	};

	const handleViewPackage = (id) => {
		debugLog(3, "[AuctionPackages][ACTION] handleViewPackage", { id: id });

		setDetailRequestId(id);
		setPageMode("liveAuction");
		debugLog(2, "[AuctionPackages][FLOW] fetchAuctionPackageByIdOrUuid started", { idOrUuid: id });
		fetchAuctionPackageByIdOrUuid(id);
	};

	// Keep retrieval error presentation at the page boundary, matching the
	// Baseline and Scenario list/detail patterns.
	useEffect(() => {
		if (retrievePackagesError) {
			withError(retrievePackagesError, fetchAuctionPackages, {
				fallbackMessage: "Retrieve Auction Packages Error",
			});
		}
	}, [fetchAuctionPackages, retrievePackagesError, withError]);

	useEffect(() => {
		if (!retrievePackageDetailError || selectedPkg || detailRequestId == null) return;

		withError(
			retrievePackageDetailError,
			() => fetchAuctionPackageByIdOrUuid(detailRequestId),
			{
				fallbackMessage: "Retrieve Auction Package Error",
				onClose: handleReloadList,
			}
		);
	}, [
		fetchAuctionPackageByIdOrUuid,
		detailRequestId,
		handleReloadList,
		retrievePackageDetailError,
		selectedPkg,
		withError,
	]);

	// --- Logging: Render and State Snapshots ---
	debugLog(9, "[AuctionPackages][FLOW] render", {
		renderCount: renderCount.current,
		selectedPkg: selectedPkg,
		showList: showList,
		pageMode: pageMode,
	});

	return (
		<>
			<PageHeader pageTitle="Auctions" actions={true}>
				{showList && isStrat() && (
					<>
						<MetreaTooltip
							content="Add New Auction Package"
						>
							<Button padding="10px" onClick={clickNewPackageHandler}>
								Add New
							</Button>
						</MetreaTooltip>
					</>
				)}
			</PageHeader>
			<Box className="pageContent">
				{showList && (
					<>
						<PackageList
							mode={pageMode}
							// setRowsPerPage={() => void 0}
							onEditPackage={handleEditPackage}
							onViewPackage={handleViewPackage}
						/>
					</>
				)}
				{!showList && (
					<>
						<PackageDetails
							mode={pageMode}
							onReloadList={handleReloadList}
							onReloadPackage={handleEditPackage}
						/>
					</>
				)}

				<PageFooter />
			</Box>
		</>
	);
};

export default AuctionPackages;
